import { useState } from "react";
import { Link } from "react-router-dom";
import { useBlogs } from "@/hooks/useBlogs";
import { useTourCategories } from "@/hooks/useTourCategories";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, Eye } from "lucide-react";

const Blog = () => {
  const { blogs, loading } = useBlogs();
  const { categories } = useTourCategories();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredBlogs = selectedCategory 
    ? blogs.filter(blog => blog.category_id === selectedCategory)
    : blogs;

  const featuredBlogs = blogs.filter(blog => blog.is_featured).slice(0, 3);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading blog...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-primary-foreground py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Tours and Adventures Blog
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
            Discover the best destinations, travel tips and unique experiences in the Dominican Republic
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredBlogs.length > 0 && (
        <section className="py-16 bg-secondary/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Featured Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBlogs.map((blog) => (
                <Card key={blog.id} className="group hover:shadow-lg transition-all duration-300">
                  {blog.main_image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={blog.main_image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default">Featured</Badge>
                      {blog.tour_categories && (
                        <Badge variant="outline">{blog.tour_categories.name}</Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
                      <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                    </h3>
                  </CardHeader>
                  <CardContent>
                    {blog.short_description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {blog.short_description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {new Date(blog.created_at).toLocaleDateString('en-US')}
                      </div>
                      <Link to={`/blog/${blog.slug}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          Read more
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => setSelectedCategory(null)}
            >
              All Posts
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          {filteredBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <Card key={blog.id} className="group hover:shadow-lg transition-all duration-300">
                  {blog.main_image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={blog.main_image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      {blog.tour_categories && (
                        <Badge variant="outline">{blog.tour_categories.name}</Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
                      <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                    </h3>
                  </CardHeader>
                  <CardContent>
                    {blog.short_description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {blog.short_description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {new Date(blog.created_at).toLocaleDateString('en-US')}
                      </div>
                      <Link to={`/blog/${blog.slug}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          Read more
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <User className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No posts available</h3>
              <p className="text-muted-foreground">
                {selectedCategory 
                  ? "No posts found in this category." 
                  : "No posts published in the blog yet."
                }
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;